import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  eignungAnlegen,
  eignungEntfernen,
  eignungen as ladeEignungen,
  eignungsgradSetzen,
  EIGNUNGSGRADE,
  EIGNUNGSGRADNAME,
  EIGNUNGSGRADSINN,
  type Eignungsgrad,
  type Eignungszeile,
  type Fahrzeugklassenzeile,
  type Ressourcenzeile,
} from '../api/ressourcen';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Liste } from '../komponenten/Liste';

/**
 * Was auf einem Modul moeglich ist (AeA-05, PRD-29, PRD-30).
 *
 * ============================================================================
 * DIE LISTE ZEIGT AUCH, WAS FEHLT
 * ============================================================================
 *
 * Der Aenderungsantrag nennt das Risiko dieser Tabelle beim Namen: Ein
 * vergessener Eintrag macht ein Modul fuer einen Baustein unsichtbar — die
 * Planung meldet dann „kein Modul verfuegbar", wo eines steht.
 *
 * Eine Maske, die nur zeigt, was schon eingetragen ist, macht genau diese
 * Luecke unsichtbar: Sie sieht bei drei Eintraegen genauso vollstaendig aus
 * wie bei vierzehn. Deshalb steht hier JEDER Baustein, und wo nichts
 * hinterlegt ist, steht das als Wort da und nicht als leere Zelle — ein
 * Fehlen laesst sich nicht vorlesen (NFA-14).
 *
 * ============================================================================
 * NICHT JEDES GEEIGNETE MODUL IST EIN GUTES MODUL (AeA-06)
 * ============================================================================
 *
 * Bremsen geht auf Modul 3 und auf Modul 7. Auf Modul 3 ist es das, wofuer
 * das Modul gebaut wurde; auf Modul 7 geht es zur Not. Ein Ja/Nein macht
 * daraus dasselbe — und eine Planung, die daraus waehlt, nimmt regelmaessig
 * das Ausweichmodul, weil es zufaellig frueher frei ist.
 *
 * Jeder Eintrag traegt deshalb einen Grad: ideal, geeignet, moeglich. Er ist
 * in der LISTE aenderbar und nicht nur beim Anlegen: Wer elf Module
 * durchgeht, aendert den Grad haeufiger als alles andere, und der Umweg ueber
 * Loeschen und Neuanlegen verloere Bemerkung und Gruppenzahl.
 *
 * Die Liste steht nach dem Grad sortiert — dieselbe Reihenfolge, in der die
 * Planung waehlt. Zwei Ordnungen fuer dieselbe Sache waeren eine zu viel.
 *
 * ============================================================================
 * DIE FAHRZEUGKLASSE IST DIE ZWEITE HAELFTE DER AUSSAGE
 * ============================================================================
 *
 * „Kurvenfahren auf Modul 5" gilt womoeglich fuer Pkw und nicht fuer Lkw.
 * Jeder Eintrag traegt deshalb seine Klasse — oder ausdruecklich keine, was
 * „mit jeder" heisst. „Nutzfahrzeug" ist die Abkuerzung fuer Transporter und
 * Lkw zusammen; sie steht in der Auswahl mit ihrer Wirkung dabei, damit
 * niemand sie fuer eine dritte Fahrzeugart haelt.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41, Auftrag #19)
 * ============================================================================
 *
 * Die Bausteine stehen als Rastertabelle in einer Karte; „Zurück zum Stamm"
 * steht in der Leiste darüber. Der neue Eintrag zu EINEM Baustein steht in
 * der Seitenleiste daneben — vorher kam er unter die vierzehn Zeilen, und wer
 * bei „Bremsen" auf „Eintrag hinzufügen" drückte, musste ihn suchen. Die Zeile,
 * zu der er gehört, ist markiert.
 */

/** Die Beschriftung je Bausteinart. Sie steht hier, damit sie ueberall gleich lautet. */
const BAUSTEINART: Readonly<Record<string, string>> = {
  begruessung: 'Begrüßung',
  theorie: 'Theorie',
  fahrmodul: 'Fahrmodul',
  pause: 'Pause',
  abschluss: 'Abschluss',
};

export function Modulkonfiguration({
  modul,
  beiSchliessen,
}: {
  readonly modul: Ressourcenzeile;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [bausteine, setzeBausteine] = useState<readonly Eignungszeile[]>([]);
  const [klassen, setzeKlassen] = useState<readonly Fahrzeugklassenzeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  /** Welcher Baustein gerade einen neuen Eintrag bekommt. */
  const [offen, setzeOffen] = useState<string | null>(null);
  const [klasse, setzeKlasse] = useState('');
  const [grad, setzeGrad] = useState<Eignungsgrad>('geeignet');
  const [gruppen, setzeGruppen] = useState('');
  const [bemerkung, setzeBemerkung] = useState('');
  /*
   * CRM-104: Welcher Eintrag in der Rückfrage steht. Er geht hart aus der
   * Tabelle; Grad, Gruppenzahl und Bemerkung sind danach weg.
   */
  const [nachfrage, setzeNachfrage] = useState<{
    readonly baustein: Eignungszeile;
    readonly eintrag: Eignungszeile['eintraege'][number];
  } | null>(null);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const antwort = await ladeEignungen(modul.id);
      setzeBausteine(antwort.bausteine);
      setzeKlassen(antwort.fahrzeugklassen);
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [modul.id]);

  useEffect(() => {
    void holen();
  }, [holen]);

  async function eintragen(baustein: string): Promise<void> {
    setzeMeldung(null);
    try {
      await eignungAnlegen(modul.id, {
        baustein,
        /*
         * Ein leeres Feld heisst AUSDRUECKLICH „mit jeder Klasse" und nicht
         * „vergessen". Es geht deshalb als null hinaus und nicht gar nicht —
         * sonst entschiede der Vorgabewert des Servers, was der Anwender
         * gemeint hat.
         */
        fahrzeugklasse: klasse === '' ? null : klasse,
        gruppenGleichzeitig: gruppen === '' ? null : Number(gruppen),
        eignungsgrad: grad,
        bemerkung: bemerkung.trim() === '' ? null : bemerkung.trim(),
      });
      setzeOffen(null);
      setzeKlasse('');
      setzeGrad('geeignet');
      setzeGruppen('');
      setzeBemerkung('');
      setzeMeldung('Der Eintrag ist angelegt.');
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  async function gradSetzen(eignung: string, neuerGrad: Eignungsgrad): Promise<void> {
    setzeMeldung(null);
    try {
      await eignungsgradSetzen(modul.id, eignung, neuerGrad);
      setzeMeldung(`Der Eintrag gilt jetzt als ${EIGNUNGSGRADNAME[neuerGrad]}.`);
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  /**
   * Aus der Rückfrage (CRM-104): Sie fängt NICHT. Der Fehler steht im
   * Pop-up, das offen bleibt — und nicht ein zweites Mal über der Liste.
   */
  async function entfernen(eignung: string): Promise<void> {
    setzeMeldung(null);
    await eignungEntfernen(modul.id, eignung);
    setzeNachfrage(null);
    setzeMeldung('Der Eintrag ist entfernt.');
    await holen();
  }

  /**
   * Der Name einer Fahrzeugklasse — bei einer Obergruppe mit dem, was sie
   * deckt. Ohne den Zusatz haelt man „Nutzfahrzeug" fuer eine eigene Art
   * neben Transporter und Lkw, und traegt es dreimal ein.
   */
  function klassenname(schluessel: string | null): string {
    if (schluessel === null) return 'jede Fahrzeugklasse';
    const klasse = klassen.find((k) => k.schluessel === schluessel);
    if (klasse === undefined) return schluessel;
    const darunter = klassen.filter((k) => k.obergruppe === schluessel);
    return darunter.length === 0
      ? klasse.bezeichnung
      : `${klasse.bezeichnung} (${darunter.map((k) => k.bezeichnung).join(' und ')})`;
  }

  /**
   * Der blosse Name einer Klasse, ohne den Zusatz, was sie deckt.
   *
   * Er steht in der versteckten Beschriftung des Grad-Feldes. Dort waere der
   * Zusatz eine Wiederholung: Er steht eine Spalte weiter links schon da, und
   * ein Vorleseprogramm liest ihn dann zweimal.
   */
  function klassenkurz(schluessel: string | null): string {
    if (schluessel === null) return 'jeder Fahrzeugklasse';
    return klassen.find((k) => k.schluessel === schluessel)?.bezeichnung ?? schluessel;
  }

  const ohneEintrag = bausteine.filter((b) => b.eintraege.length === 0);
  const offenerBaustein = bausteine.find((b) => b.baustein === offen);

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        filter={
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Zurück zum Stamm
          </button>
        }
        hinweise={
          <>
            {meldung !== null && (
              <p className="hinweiszeile" role="status">
                {meldung}
              </p>
            )}
            {/*
             * DIE LUECKE STEHT OBEN UND ALS ZAHL. Wer sie erst beim Durchsehen der
             * vierzehn Zeilen bemerkt, bemerkt sie nicht.
             */}
            {!laedt && (
              <p className={ohneEintrag.length === 0 ? 'feld__hilfe' : 'warnung'} role="status">
                {ohneEintrag.length === 0
                  ? `Für alle ${String(bausteine.length)} Bausteine ist etwas hinterlegt.`
                  : `Für ${String(ohneEintrag.length)} von ${String(bausteine.length)} Bausteinen ist `
                    + `nichts hinterlegt: ${ohneEintrag.map((b) => b.bezeichnung).join(', ')}.`}
              </p>
            )}
          </>
        }
        vorschauBeschriftung="Neuer Eintrag"
        vorschau={
          offen === null ? (
            <p className="feld__hilfe">
              „Eintrag hinzufügen" an einer Zeile öffnet hier den Eintrag für diesen Baustein:
              mit welcher Fahrzeugklasse er auf diesem Modul geht, wie gut und mit wie vielen
              Gruppen zugleich.
            </p>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void eintragen(offen);
              }}
            >
              <div className="karte__titel">
                <h2>Eintrag für {offenerBaustein?.bezeichnung ?? offen}</h2>
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eignungKlasse">
                  Möglich mit
                </label>
                <select
                  id="eignungKlasse"
                  className="feld__eingabe"
                  value={klasse}
                  onChange={(e) => {
                    setzeKlasse(e.target.value);
                  }}
                >
                  <option value="">jeder Fahrzeugklasse</option>
                  {klassen.map((k) => (
                    <option key={k.schluessel} value={k.schluessel}>
                      {klassenname(k.schluessel)}
                    </option>
                  ))}
                </select>
                <p className="feld__hilfe">
                  „Jeder Fahrzeugklasse“ ist eine Aussage und kein leeres Feld: Der Baustein geht
                  hier mit allem. Wo das nicht stimmt, gehört je Klasse ein eigener Eintrag her.
                </p>
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eignungGrad">
                  Wie gut
                </label>
                <select
                  id="eignungGrad"
                  className="feld__eingabe"
                  value={grad}
                  onChange={(e) => {
                    setzeGrad(e.target.value as Eignungsgrad);
                  }}
                >
                  {EIGNUNGSGRADE.map((g) => (
                    <option key={g} value={g}>
                      {EIGNUNGSGRADNAME[g]}
                    </option>
                  ))}
                </select>
                {/* AeA-06 */}
                <p className="feld__hilfe">{EIGNUNGSGRADSINN[grad]}</p>
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eignungGruppen">
                  Gruppen gleichzeitig
                </label>
                <input
                  id="eignungGruppen"
                  type="number"
                  min="1"
                  className="feld__eingabe feld__eingabe--schmal"
                  value={gruppen}
                  onChange={(e) => {
                    setzeGruppen(e.target.value);
                  }}
                />
                {/* RES-51 */}
                <p className="feld__hilfe">
                  Leer heißt: so viele wie am Modul stehen. Ein Wert hier gilt nur für diesen
                  Baustein — dieselbe Fläche trägt beim Bremsen zwei Gruppen und beim Driften eine.
                </p>
              </div>

              <div className="feld">
                <label className="feld__beschriftung" htmlFor="eignungBemerkung">
                  Bemerkung
                </label>
                <input
                  id="eignungBemerkung"
                  className="feld__eingabe"
                  value={bemerkung}
                  onChange={(e) => {
                    setzeBemerkung(e.target.value);
                  }}
                  placeholder="nur bei trockener Fahrbahn"
                />
              </div>

              <div className="knopfreihe knopfreihe--abschluss">
                <button
                  type="button"
                  className="knopf"
                  onClick={() => {
                    setzeOffen(null);
                  }}
                >
                  Verwerfen
                </button>
                <button type="submit" className="knopf knopf--haupt">
                  Eintragen
                </button>
              </div>
            </form>
          )
        }
      >
        <div className="karte__titel">
          <h2>
            {modul.kennung} · {modul.bezeichnung}
          </h2>
        </div>
        <p className="hilfe">
          Was auf diesem Modul möglich ist. Jede Zeile ist ein Baustein; ein Eintrag sagt, mit
          welcher Fahrzeugklasse er hier geht. Was nicht eingetragen ist, ist auf diesem Modul
          nicht planbar — die Tagesplanung findet es nicht.
        </p>

        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">Mögliche Bausteine auf diesem Modul</caption>
            <thead>
              <tr>
                <th scope="col">Baustein</th>
                <th scope="col">Art</th>
                <th scope="col">Möglich mit</th>
                <th scope="col">Wie gut</th>
                <th scope="col">Gruppen gleichzeitig</th>
                <th scope="col">
                  <span className="nur-vorlesen">Bedienung</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {bausteine.map((b) => (
                <tr
                  key={b.baustein}
                  className={offen === b.baustein ? 'liste__zeile--gewaehlt' : undefined}
                >
                  <td className="liste__name">{b.bezeichnung}</td>
                  <td>{BAUSTEINART[b.art] ?? b.art}</td>
                  <td>
                    {b.eintraege.length === 0 ? (
                      <span className="marke marke--inaktiv">nicht hinterlegt</span>
                    ) : (
                      <ul className="kanalliste">
                        {b.eintraege.map((e) => (
                          <li key={e.id}>
                            {klassenname(e.fahrzeugklasse)}
                            {e.bemerkung !== null && (
                              <span className="hilfe"> · {e.bemerkung}</span>
                            )}{' '}
                            <button
                              type="button"
                              className="knopf knopf--eingriff knopf--klein"
                              onClick={() => {
                                setzeNachfrage({ baustein: b, eintrag: e });
                              }}
                            >
                              Entfernen …
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    {b.eintraege.length === 0 ? (
                      '—'
                    ) : (
                      /*
                       * DER GRAD IST HIER AENDERBAR UND NICHT NUR IM FORMULAR.
                       * Wer elf Module durchgeht, aendert ihn haeufiger als
                       * alles andere; der Umweg ueber Loeschen und Neuanlegen
                       * verloere Bemerkung und Gruppenzahl (AeA-06).
                       */
                      <ul className="kanalliste">
                        {b.eintraege.map((e) => (
                          <li key={e.id}>
                            <label className="nur-vorlesen" htmlFor={`grad-${e.id}`}>
                              Wie gut {b.bezeichnung} mit {klassenkurz(e.fahrzeugklasse)} geht
                            </label>
                            <select
                              id={`grad-${e.id}`}
                              className="liste__filter"
                              value={e.eignungsgrad}
                              onChange={(aenderung) => {
                                void gradSetzen(
                                  e.id,
                                  aenderung.target.value as Eignungsgrad,
                                );
                              }}
                            >
                              {EIGNUNGSGRADE.map((g) => (
                                <option key={g} value={g}>
                                  {EIGNUNGSGRADNAME[g]}
                                </option>
                              ))}
                            </select>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    {b.eintraege.length === 0
                      ? '—'
                      : b.eintraege
                          .map((e) =>
                            e.gruppenGleichzeitig === null
                              ? 'wie am Modul'
                              : String(e.gruppenGleichzeitig),
                          )
                          .join(', ')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      onClick={() => {
                        setzeOffen(offen === b.baustein ? null : b.baustein);
                        setzeKlasse('');
                        setzeGruppen('');
                        setzeBemerkung('');
                      }}
                    >
                      {offen === b.baustein ? 'Abbrechen' : 'Eintrag hinzufügen'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {bausteine.length === 0 && !laedt && (
          <div className="liste__leer" role="status">
            Es ist kein Baustein angelegt. Ohne Bausteine lässt sich nichts hinterlegen.
          </div>
        )}
      </Liste>

      {nachfrage !== null && (
        <Rueckfrage
          frage="Eintrag entfernen?"
          unterzeile={`${nachfrage.baustein.bezeichnung} · ${klassenname(nachfrage.eintrag.fahrzeugklasse)}`}
          tat="Entfernen"
          beiBestaetigen={() => entfernen(nachfrage.eintrag.id)}
          beiSchliessen={() => {
            setzeNachfrage(null);
          }}
        >
          {/* PRD-29, PRD-30: Was nicht eingetragen ist, findet die Tagesplanung nicht. */}
          <p>
            {nachfrage.baustein.eintraege.length === 1
              ? `Es ist der einzige Eintrag: ${nachfrage.baustein.bezeichnung} ist danach auf ${modul.kennung} nicht mehr planbar.`
              : `${nachfrage.baustein.bezeichnung} geht auf ${modul.kennung} danach nicht mehr mit ${klassenkurz(nachfrage.eintrag.fahrzeugklasse)}.`}{' '}
            Grad, Gruppenzahl und Bemerkung gehen verloren; zurück kommt der Eintrag nur, wenn
            man ihn neu anlegt.
          </p>
        </Rueckfrage>
      )}
    </>
  );
}
